import React, { useEffect, useState } from 'react'
import {
  MyButtonGroupV2,
  MyTabPanel,
  MyTabView,
} from '@interstellar-component'
import { XClose, AlertTriangle } from '@untitled-ui/icons-react'
import Result from './Result'
import Billing from './Billing'
import { formatCompanyDetailSubtitle, useCompany } from '../../Context'
import Information from './Information'
import MyMemberStatusChip, { AgreementStatusChip, memberStatusOf } from '../MyMemberStatusChip'
import { BillingService } from '../../billing.service'
import { formatIDR, derivePaymentAlert } from '../../billing.format'

const TAB_TO_DETAIL_TYPE = {
  result: 'result',
  billing: 'billing',
  information: 'information',
}

function MyDetailSlider() {
  const {
    handleCurrentSlider,
    currentSlider,
    fetchCompanyDetail,
    companyDetail,
    isLoadingCompanyDetail,
    billingRefreshKey,
    openInvoicePanel,
    closeBillingPanels,
  } = useCompany()

  const [tab, setTab] = useState('result')
  // Payment alert surfaced in the slider header so a pending / suspended /
  // outstanding invoice is visible from any tab — no need to dig into Billing
  // → invoice history. Mode-agnostic: getSubscription / getInvoices go through
  // the BillingService adapter (→ SAW in cloud mode). Re-runs on billingRefreshKey
  // so it clears after the user pays or cancels.
  const [paymentAlert, setPaymentAlert] = useState(null)

  useEffect(() => {
    if (!currentSlider.status || currentSlider.current !== 'details-slider') return
    const { id } = currentSlider
    if (id == null || id === '') return
    const type = TAB_TO_DETAIL_TYPE[tab] ?? 'result'
    fetchCompanyDetail(id, { type })
  }, [currentSlider, tab, fetchCompanyDetail])

  useEffect(() => {
    const id = currentSlider?.id
    if (!currentSlider?.status || currentSlider?.current !== 'details-slider' || !id) {
      setPaymentAlert(null)
      return undefined
    }
    let active = true
    Promise.all([
      BillingService.getSubscription(id).catch(() => null),
      BillingService.getInvoices(id, { page: 1, limit: 50 }).catch(() => null),
    ]).then(([s, inv]) => {
      if (!active) return
      const subscription =
        s && Object.prototype.hasOwnProperty.call(s, 'data') ? s.data : s || null
      const invoices = inv?.data?.data ?? inv?.data ?? []
      setPaymentAlert(derivePaymentAlert({ subscription, invoices }))
    })
    return () => {
      active = false
    }
  }, [currentSlider?.id, currentSlider?.status, currentSlider?.current, billingRefreshKey])

  const handlePayNow = () => {
    if (!paymentAlert?.invoiceId) return
    setTab('billing')
    openInvoicePanel(paymentAlert.invoiceId)
  }

  const title =
    companyDetail?.company_information?.name ??
    companyDetail?.name ??
    companyDetail?.company_information?.legal_name ??
    companyDetail?.legal_name ??
    '—'
  const subtitle = formatCompanyDetailSubtitle(companyDetail)
  // Header chip follows the membership lifecycle (verification_status first,
  // enrollment phase as fallback) — same derivation as the list.
  const memberStatus = memberStatusOf(companyDetail)
  // Initial-load state: no data yet AND a fetch is in-flight. Tab switches
  // refetch but `companyDetail` still holds the previous payload, so we keep
  // the header visible to avoid the title flickering to "…" each time.
  const isInitialLoading = isLoadingCompanyDetail && !companyDetail

  return (
    <div className="flex h-screen w-[375px] flex-col">
      <header className="relative flex flex-col px-4">
        <div className="relative flex items-start gap-x-4 pb-4 pt-8">
          <button
            type="button"
            aria-label="Close"
            className="absolute right-[12px] top-[12px] flex h-11 w-11 items-center justify-center rounded-lg p-2 text-gray-light/400"
          >
            <XClose
              onClick={() => handleCurrentSlider(null)}
              className="size-6"
              stroke="currentColor"
            />
          </button>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <p className="text-xl-semibold text-gray-light/900">
                  {isInitialLoading ? '…' : title}
                </p>
                {subtitle ? (
                  <p className="text-md-regular text-gray-light/600">{subtitle}</p>
                ) : null}
              </div>
              {/* Membership + agreement side by side: an "Approved" member
                  can still be "Waiting CLIK signature" (sequential DocuSign). */}
              {!isInitialLoading ? (
                <div className="flex flex-wrap items-center gap-2">
                  {memberStatus ? <MyMemberStatusChip status={memberStatus} /> : null}
                  <AgreementStatusChip company={companyDetail} />
                </div>
              ) : null}
            </div>
            <MyButtonGroupV2
              buttons={[
                { label: 'Result', value: 'result' },
                { label: 'Billing', value: 'billing' },
                { label: 'Information', value: 'information' },
              ]}
              value={tab}
              // Leaving a tab closes any open invoice/top-up/subscribe panel —
              // the payment detail panel used to stay open over Result/Information
              // (Everest #86exwf565). Guard on value!==tab so the programmatic
              // setTab('billing') in handlePayNow (which then opens the panel) is
              // unaffected.
              onChange={(value) => {
                if (value !== tab) closeBillingPanels()
                setTab(value)
              }}
            />
          </div>
        </div>
      </header>

      {paymentAlert ? (
        <div
          className={`mx-4 mb-3 rounded-lg border p-3 ${
            paymentAlert.severity === 'error'
              ? 'border-error/200 bg-error/50'
              : 'border-warning/200 bg-warning/50'
          }`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle
              className={`mt-0.5 h-4 w-4 shrink-0 ${
                paymentAlert.severity === 'error' ? 'text-error/600' : 'text-warning/600'
              }`}
              stroke="currentColor"
            />
            <div className="flex-1">
              <p
                className={`text-sm-semibold ${
                  paymentAlert.severity === 'error' ? 'text-error/700' : 'text-warning/700'
                }`}
              >
                {paymentAlert.title}
                {paymentAlert.amountIdr != null ? ` · ${formatIDR(paymentAlert.amountIdr)}` : ''}
              </p>
              <p
                className={`mt-0.5 text-xs-regular ${
                  paymentAlert.severity === 'error' ? 'text-error/700' : 'text-warning/700'
                }`}
              >
                {paymentAlert.message}
              </p>
              {paymentAlert.invoiceId ? (
                <button
                  type="button"
                  onClick={handlePayNow}
                  className="mt-2 rounded-lg bg-brand/600 px-3 py-1.5 text-xs-semibold text-white shadow-xs hover:bg-brand/700"
                >
                  Pay now
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex-1 overflow-hidden">
        <MyTabView value={tab}>
          <MyTabPanel value="result">
            <Result data={companyDetail} loading={isLoadingCompanyDetail} />
          </MyTabPanel>
          <MyTabPanel value="billing">
            <Billing companyId={currentSlider?.id} />
          </MyTabPanel>
          <MyTabPanel value="information">
            <Information data={companyDetail} loading={isLoadingCompanyDetail} />
          </MyTabPanel>
        </MyTabView>
      </div>
    </div>
  )
}

export default MyDetailSlider
